import { useState } from "react";

import { supabaseClient } from "@/config/supabase";

interface UseFacebookOAuthReturn {
  handleFacebookSignIn: () => void;
  handleFacebookSignUp: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for Facebook OAuth with Supabase (web).
 *
 * Uses Supabase's native OAuth redirect flow (same as mobile app uses native SDK).
 * This properly handles the Facebook OAuth handshake and returns a valid ID token.
 *
 * Flow:
 * 1. User clicks "Sign in/Sign up with Facebook" button
 * 2. Redirects to Supabase OAuth endpoint (handles Facebook OAuth)
 * 3. Facebook signs them in and Supabase redirects back to /auth/callback
 * 4. Session is established automatically by Supabase
 * 5. User is logged in or account is created in omnibus
 */
export const useFacebookOAuth = (): UseFacebookOAuthReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Initiate Facebook OAuth via Supabase redirect flow.
   * Redirects to Supabase auth endpoint which handles the Facebook OAuth handshake.
   */
  const initiateFacebookOAuth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { error: oauthError } = await supabaseClient.auth.signInWithOAuth({
        provider: "facebook",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          scopes: "public_profile,email",
        },
      });

      if (oauthError) {
        console.error("[useFacebookOAuth] OAuth error:", oauthError);
        setError(oauthError.message);
        setIsLoading(false);
        return;
      }

      // Redirect will happen automatically - page will unload
    } catch (err) {
      console.error("[useFacebookOAuth] Unexpected error:", err);
      setError("An unexpected error occurred");
      setIsLoading(false);
    }
  };

  const handleFacebookSignIn = () => {
    initiateFacebookOAuth();
  };

  const handleFacebookSignUp = () => {
    initiateFacebookOAuth();
  };

  return {
    handleFacebookSignIn,
    handleFacebookSignUp,
    isLoading,
    error,
  };
};
